import {
  AddCircleOutline,
  DeleteOutline,
  PersonOutline,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { blue, blueGrey, grey } from "@mui/material/colors";
import React, { useMemo } from "react";
import { useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import Flx from "../../components/layout/Flx";
import RffAddressInputField from "../../components/finalForm/inputs/RffAddressInputField";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffEmailField from "../../components/finalForm/inputs/RffEmailField";
import RffPhoneField from "../../components/finalForm/inputs/RffPhoneField";
import RffSelectToggleField from "../../components/finalForm/inputs/RffSelectToggleField";
import RffSsnField from "../../components/finalForm/inputs/RffSsnField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffConditional from "../../components/finalForm/shared/RffConditional";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import { selectOptionsCitizenshipStatus } from "../../constants/selectOptions/selectOptionsCitizenshipStatus";
import TitledLoanSection from "./TitledLoanSection";
import Txt from "../../components/typography/Txt";
import { getFirstLetterOfString } from "../../utils/strings/getFirstLetterOfString";
import { isNil } from "lodash";

const BorrowersSection = ({ testing }) => {
  const { change } = useForm();

  const onCitizenshipStatusChange = (fieldName, v) => {
    if (v === "US Citizen") {
      change(`${fieldName}.borrowerHasSSN`, true);
    } else {
      change(`${fieldName}.borrowerHasSSN`, false);
    }
  };

  return (
    <TitledLoanSection
      collapsable
      variant="h2"
      icon={<PersonOutline className="thin" />}
      title="Borrower Information"
      bodyTopPadding={2}
    >
      <FieldArray name={"borrowers"}>
        {({ fields: arrayFields, meta }) => {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {arrayFields.map((fieldName, index) => (
                <BorrowerCard
                  key={fieldName}
                  fieldName={fieldName}
                  index={index}
                  isFirst={index === 0}
                  canRemove={arrayFields.length > 1}
                  onRemove={() => arrayFields.remove(index)}
                  onCitizenshipStatusChange={onCitizenshipStatusChange}
                  testing={testing}
                />
              ))}

              <Button
                variant="outlined"
                size="large"
                startIcon={
                  <AddCircleOutline
                    fontSize="small"
                    sx={{ fontSize: "18px !important" }}
                    className="thin"
                  />
                }
                onClick={
                  () => arrayFields.push({})
                  // arrayFields.push({
                  //   firstName: "",
                  //   lastName: "",
                  //   emailAddress: "",
                  //   phone: "",
                  //   dateOfBirth: "",
                  //   citizenshipStatus: "",
                  //   maritalStatus: "",
                  //   borrowerHasSSN: false,
                  // })
                }
                sx={{
                  py: 1.5,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  lineHeight: 1,
                  "&:hover": {
                    borderWidth: 1,
                    borderStyle: "dashed",
                    backgroundColor: grey[50],
                  },
                }}
              >
                Add Co-Borrower
              </Button>
            </Box>
          );
        }}
      </FieldArray>
    </TitledLoanSection>
  );
};

const BorrowerCard = ({
  fieldName,
  index,
  isFirst,
  canRemove,
  onRemove,
  onCitizenshipStatusChange,
  testing,
}) => {
  const { values } = useFormState();
  const avatarValue = useMemo(() => {
    if (
      isNil(values.borrowers[index].firstName) ||
      isNil(values.borrowers[index].lastName)
    ) {
      return <PersonOutline />;
    }

    return `${getFirstLetterOfString(values.borrowers[index].firstName)}${getFirstLetterOfString(values.borrowers[index].lastName)}`;
  }, [values.borrowers[index].firstName, values.borrowers[index].lastName]);
  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        position: "relative",
        boxShadow: "none",
        border: `1px solid #e2e8f0`,
        borderLeft: isFirst
          ? `1px solid #e2e8f0`
          : `4px solid ${blueGrey[600]}`,
        // backgroundColor: isFirst ? "#ffffff" : grey[50],
      }}
    >
      {/* Header */}
      <Flx
        jb
        ai="center"
        sx={{ mb: 4, borderBottom: `2px solid #e2e8f0`, pb: 2 }}
      >
        <Flx ai="center" g={1.5}>
          <Avatar
            sx={
              {
                // width: 40,
                // height: 40,
                // borderRadius: "50%",
                // backgroundColor: isFirst ? "primary.main" : grey[300],
                // display: "flex",
                // alignItems: "center",
                // justifyContent: "center",
                // color: isFirst ? "white" : grey[700],
              }
            }
          >
            {/* <PersonOutline /> */}
            {avatarValue}
          </Avatar>
          <Flx column>
            <Txt bold sx={{ lineHeight: 1.2 }}>
              {isFirst ? "Primary Borrower" : `Co-Borrower ${index}`}
            </Txt>
            <Txt secondary>
              {isFirst ? "Main applicant" : "Additional applicant"}
            </Txt>
          </Flx>
        </Flx>

        {canRemove && (
          <Tooltip title="Remove Co-Borrower">
            <IconButton onClick={onRemove} size="small" color="error">
              <DeleteOutline className="thin" />
            </IconButton>
          </Tooltip>
        )}
      </Flx>

      {/* <Divider sx={{ mb: 3 }} /> */}

      {/* Form Fields */}
      <RffGroup suppressBottomMargin>
        <RffTextField
          name={`${fieldName}.firstName`}
          label="First Name"
          required
          size={6}
        />
        <RffTextField
          name={`${fieldName}.lastName`}
          label="Last Name"
          required
          size={6}
        />
        <RffEmailField
          size={4}
          name={`${fieldName}.emailAddress`}
          label="Email"
        />
        <RffPhoneField size={4} name={`${fieldName}.phone`} label="Phone" />

        <RffDateField
          size={4}
          name={`${fieldName}.dateOfBirth`}
          label="Date of birth"
        />
        <RffConditional
          testing={testing}
          operatorForMultiple={"OR"}
          rules={[
            {
              field: `${fieldName}.citizenshipStatus`,
              operator: "==",
              value: "US Citizen",
            },
            {
              field: `${fieldName}.citizenshipStatus`,
              operator: "==",
              value: "Foreign National with SSN",
            },
            {
              field: `${fieldName}.citizenshipStatus`,
              operator: "==",
              value: "Foreign National",
            },
          ]}
        >
          <RffAddressInputField
            name={`${fieldName}.primaryAddress`}
            label="Borrower Primary Address"
          />
        </RffConditional>
        <RffSelectToggleField
          name={`${fieldName}.citizenshipStatus`}
          label="Borrower Citizenship"
          onChange={(v) => onCitizenshipStatusChange(fieldName, v)}
          options={selectOptionsCitizenshipStatus}
        />

        <RffConditional
          testing={testing}
          operatorForMultiple={"OR"}
          rules={[
            {
              field: `${fieldName}.citizenshipStatus`,
              operator: "==",
              value: "Foreign National Non-Resident",
            },
            {
              field: `${fieldName}.citizenshipStatus`,
              operator: "==",
              value: "Other - Options do not apply",
            },
          ]}
        >
          <RffTextField
            name={`${fieldName}.nonUSborrowerAddress`}
            label="Enter non-US address"
          />
        </RffConditional>
        <RffSelectToggleField
          size={4}
          name={`${fieldName}.maritalStatus`}
          label="Marital Status"
          options={["Married", "Not Married"]}
        />
        <RffBooleanToggleField
          name={`${fieldName}.borrowerHasSSN`}
          size={4}
          label="Does the borrower have a US issued SSN?"
        />
        <RffConditional
          testing={testing}
          field={`${fieldName}.borrowerHasSSN`}
          value={true}
        >
          <RffSsnField
            size={4}
            name={`${fieldName}.borrowerSSN`}
            label="Social Security Number"
          />
        </RffConditional>
      </RffGroup>
    </Card>
  );
};

export default BorrowersSection;
